import { _t } from "app/i18n/i18n";
import { isMobile } from "lib/hooks/useIsMobile";
import { JAPIAutoCompleteLoader } from "api/publications/JAPIAutoCompleteLoader";
import { addParam, push } from "api/search/filterUrlMaker";
import { KeywordSuggestions } from "components/SearchBar/KeywordSuggestions";
import React from "react";
import { Col, ListGroup, Row, Container } from "react-bootstrap";
import { uuid } from "lib/utils";
import { getGRP_JAPI_Url } from "utils/htmlReportsUtils";
import { CompanySuggestions } from "./CompanySuggestions";
import "./autocomplete.css";

export function AutoSuggestions({
                                  keyword,
                                  setKeyword,
                                  setIsSearching,
                                  forHtmlReport = false,
                                  chatOpen,
                                  panelWidth,
                                  onNairaSearch,
                                  nairaMode,
                                  setNairaMode,
                                  suppressBlurRef,
                                }) {
  return (
    <JAPIAutoCompleteLoader
      keyword={keyword}
      setIsSearching={setIsSearching}
      render={(suggestions) => {
        const analysts = suggestions.filter((suggest) => suggest.CategoryName === "Analyst");
        const companies = suggestions.filter((suggest) => suggest.CategoryName === "CompanyWithTicker");
        const periodicals = suggestions.filter((suggest) => suggest.CategoryName === "Report Titles");
        const tradeIdeas = suggestions.filter((suggest) => suggest.CategoryName === "Trade Ideas");
        const themes = suggestions.filter((suggest) => suggest.CategoryName === "Theme").sort(sortAscending);
        const regions = suggestions.filter((suggest) => suggest.CategoryName === "Region");
        const countries = suggestions.filter((suggest) => suggest.CategoryName === "Country");

        const renderList = (list) =>
          list.map((suggestion, key) => (
            <Suggestion
              key={uuid(key)}
              suggestion={suggestion}
              setKeyword={setKeyword}
              forHtmlReport={forHtmlReport}
              nairaMode={nairaMode}
              onNairaSearch={onNairaSearch}
            />
          ));

        const countryRegionColNum =
          analysts?.length && companies?.length && periodicals?.length && themes?.length ? 1 : 2;

        return (
          <Container
            fluid
            onMouseDown={() => {
              if (suppressBlurRef) suppressBlurRef.current = true;
            }}
            style={
              chatOpen
                ? {
                  position: "absolute",
                  left: 0,
                  right: panelWidth,
                  maxWidth: `calc(100vw - ${panelWidth + 14}px)`,
                  transition: "right 0.3s",
                }
                : undefined
            }
            className={`${isMobile() ? "pb-3 mb-4 mt-md-0 border-bottom shadow bg-white" : "auto-index"}`}
          >
            <Row className={`${isMobile() ? "justify-content-end" : "auto-suggestion-row"}`}>
              <Col
                xl="2"
                lg="2"
                md="12"
                sm="12"
                className={`${
                  isMobile()
                    ? ""
                    : "bg-white pb-3 shadow-bottom shadow-left border-left border-bottom auto-suggestion-col"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <KeywordSuggestions
                    keyword={keyword}
                    setKeyword={setKeyword}
                    forHtmlReport={forHtmlReport}
                    onNairaSearch={onNairaSearch}
                    onSubmit={onKeywordSubmit(setKeyword, forHtmlReport)}
                    nairaMode={nairaMode}
                    setNairaMode={setNairaMode}
                  />
                </ListGroup>
              </Col>

              {/* Analysts */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  analysts.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Analysts")} />
                  {renderList(analysts)}
                </ListGroup>
              </Col>

              {/* Regions */}
              <Col
                xl={countryRegionColNum}
                lg="1"
                md="6"
                sm="12"
                className={`${
                  regions.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Regions")} />
                  <ListGroup className="list-group-flush d-block">{renderList(regions)}</ListGroup>
                </ListGroup>
              </Col>

              {/* Countries */}
              <Col
                xl={countryRegionColNum}
                lg="1"
                md="6"
                sm="12"
                className={`${
                  countries.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Countries")} />
                  <ListGroup className="list-group-flush d-block">{renderList(countries)}</ListGroup>
                </ListGroup>
              </Col>

              {/* Trade Ideas */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  tradeIdeas.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Trade ideas (FX/Rates)")} />
                  {renderList(tradeIdeas)}
                </ListGroup>
              </Col>

              {/* Themes */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  themes.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Themes")} />
                  {renderList(themes)}
                </ListGroup>
              </Col>

              {/* Companies */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  companies.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <CompanySuggestions
                    companies={companies}
                    keyword={keyword}
                    setKeyword={setKeyword}
                    nCompanyColumns="1"
                    forHtmlReport={forHtmlReport}
                    nairaMode={nairaMode}
                    onNairaSearch={onNairaSearch}
                  />
                </ListGroup>
              </Col>

              {/* Periodicals */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  periodicals.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Periodicals")} />
                  <ListGroup className="list-group-flush d-block" style={{ columnCount: 1 }}>
                    {renderList(periodicals)}
                  </ListGroup>
                </ListGroup>
              </Col>
            </Row>
          </Container>
        );
      }}
    />
  );
}

function onKeywordSubmit(setKeyword, forHtmlReport) {
  return (categoryName, value) => {
    setKeyword("");
    const categoryWithLang = categoryName || "q";
    return forHtmlReport
      ? window.open(`${getGRP_JAPI_Url()}/research/m/Home?${categoryWithLang}=${value}`)
      : push(addParam(categoryWithLang, value));
  };
}

export function Suggestion({ suggestion, setKeyword, forHtmlReport, onSubmit, nairaMode, onNairaSearch }) {
  return (
    <ListGroup.Item
      as="a"
      onMouseDown={() => {
        if (onSubmit) {
          onSubmit(suggestion.CategoryName, suggestion.CategoryValue);
        } else if (nairaMode && onNairaSearch) {
          setKeyword("");
          const valueForNaira = suggestion.CategoryValue.replace(/^"|"$/g, "");
          onNairaSearch(valueForNaira);
        } else {
          onAutoCompleteClick(suggestion, setKeyword, forHtmlReport);
        }
      }}
      className="cursor-pointer py-1 text-info d-block"
    >
      {suggestion.label || suggestion.CategoryValue}
    </ListGroup.Item>
  );
}

const onAutoCompleteClick = (suggestion, setKeyword, forHtmlReport) => {
  const category = suggestion.CategoryName || "";
  const categoryWithLang = category + (suggestion.CategoryLang && suggestion.CategoryLang === "ja" ? "-JA" : "");
  setKeyword("");
  const suggestionValue =
    suggestion.CategoryName === "companies.securityType"
      ? `${suggestion.CategoryValue}_PD`
      : suggestion.CategoryValue;
  return forHtmlReport
    ? window.open(`${getGRP_JAPI_Url()}/research/m/Home?${categoryWithLang}=${suggestionValue}`)
    : push(addParam(categoryWithLang, suggestionValue));
};

function Header({ label }) {
  return <ListGroup.Item className="h4 font-weight-lighter pl-2 pb-2 pt-3 mb-0 ">{label}</ListGroup.Item>;
}

const sortAscending = (a, b) => {
  if (a.CategoryValue < b.CategoryValue) return -1;
  if (a.CategoryValue > b.CategoryValue) return 1;
  return 0;
};